import React, { useEffect, useMemo, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import {
  DefaultTheme,
  ThemeProvider as OriginalThemeProvider,
} from "styled-components/native";
import { useThemeStore } from "@redux/slices/themeSlice";
import { DEVICE_SIZE } from "@utils/index";
import * as Device from "expo-device";
import { isIpad } from "@utils/design-tokens";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { Platform } from "react-native";
import { useSelector } from "react-redux";
import useWebOrientation from "../../hooks/useWebOrientation";

interface Props {
  children?: React.ReactElement;
}

const ThemeProvider = ({ children }: Props) => {
  if (Platform.OS === "web") {
    const mediaQuery = window.matchMedia("print");
    if (mediaQuery.matches) {
      return;
    }
  }

  const theme = useThemeStore();
  const orientation = useWebOrientation();
  const prevOrientationRef = useRef(orientation);
  const isIFrame = useSelector((state) => state?.userInfo?.user?.iframe_status);

  const mobileQuery = useMediaQuery({ maxWidth: 767 });
  const tabletQuery = isIFrame
    ? useMediaQuery({ minWidth: 768, maxWidth: 991 })
    : useMediaQuery({ minWidth: 767, maxWidth: 1279 });
  const desktopQuery = isIFrame
    ? useMediaQuery({ minWidth: 992 })
    : useMediaQuery({ minWidth: 1279 });
  let href: string | undefined;
  if (Platform.OS === "web") {
    href = window.location.href;
  }
  const { width } = DEVICE_SIZE;
  const { isFold } = useIsFoldable();
  const isMobile = isFold || mobileQuery || width <= 767;
  const isTablet =
    !isFold &&
    ((!isIFrame && tabletQuery) ||
      (isIFrame ? width > 767 && width <= 991 : width > 767 && width <= 1279) ||
      Device?.osName === "iPadOS" ||
      isIpad());
  const isDesktop =
    !isFold &&
    (isIFrame ? desktopQuery || width > 991 : desktopQuery || width > 1279) &&
    !(Device?.osName === "iPadOS" || isIpad());
  const isLargeScreen = isDesktop && !isTablet && !isMobile;

  const media = {
    isMobile,
    isTablet,
    isDesktop,
    isLargeScreen,
    isFold,
  };

  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    const background = theme["core-01"];
    const color = theme["core-05"];
    if (typeof background === "string") {
      document.documentElement.style.setProperty("--input-bg", background);
    }
    if (typeof color === "string") {
      document.documentElement.style.setProperty("--input-color", color);
    }
  }, [theme]);

  useEffect(() => {
    if (!isIFrame && Platform.OS === "web") {
      if (
        !orientation ||
        !prevOrientationRef.current ||
        prevOrientationRef.current === orientation
      ) {
        prevOrientationRef.current = orientation;
        return;
      }

      prevOrientationRef.current = orientation;
      window.location.reload();
    }
  }, [orientation, isIFrame]);

  useEffect(() => {
    if (
      !isIFrame &&
      Platform.OS === "web" &&
      ((mobileQuery && width > 767) ||
        (tabletQuery && width <= 767 && width > 1279) ||
        (desktopQuery && width < 1279))
    ) {
      if (typeof window !== "undefined" && window.location) {
        if (!__DEV__) window.location.reload();
      }
    }
  }, [isMobile, isTablet, isDesktop, isLargeScreen, href]);

  const mediaTheme: DefaultTheme = useMemo(
    () => ({ ...theme, ...media }),
    [theme]
  );

  return (
    <OriginalThemeProvider theme={mediaTheme}>{children}</OriginalThemeProvider>
  );
};

export default ThemeProvider;
